import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRightIcon, 
  LocationMarkerIcon, 
  ChartBarIcon, 
  ClockIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  LightningBoltIcon
} from '@heroicons/react/outline';

const HeroSection = () => {
  // State for carousel
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [direction, setDirection] = useState(1);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  
  // Enhanced data for carousel slides
  const carouselSlides = [
    {
      image: "/assets/site_carrefour1.jpg",
      badge: "PRIME LOCATION",
      title: "Écrans LED Grand Format",
      description: "Attirez tous les regards avec nos écrans HD dans les zones à fort trafic",
      icon: <LightningBoltIcon className="h-4 w-4" />,
      stats: [
        { value: "+30%", label: "Visibilité" },
        { value: "24/7", label: "Diffusion" }
      ],
      color: "#E84E0E"
    },
    {
      image: "/assets/site_carrefour2.jpg",
      badge: "INTÉRIEUR PREMIUM",
      title: "Espaces Shopping Stratégiques",
      description: "Touchez votre cible au moment d'achat dans nos centres commerciaux",
      icon: <UserGroupIcon className="h-4 w-4" />,
      stats: [
        { value: "+15K", label: "Visiteurs/Jour" },
        { value: "92%", label: "Mémorisation" }
      ],
      color: "#ff6a34"
    },
    {
      image: "/assets/site_4.jpg",
      badge: "CAMPAGNES FLEXIBLES",
      title: "Réservez en Quelques Clics",
      description: "Démarrez votre campagne dès demain avec notre système ultra-rapide",
      icon: <CurrencyDollarIcon className="h-4 w-4" />,
      stats: [
        { value: "-40%", label: "Budget vs TV" },
        { value: "24h", label: "Délai" }
      ],
      color: "#ff4500"
    }
  ];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle autoplay
  useEffect(() => {
    let interval;
    if (autoplay) {
      interval = setInterval(() => {
        setDirection(1);
        setCurrentSlide((prev) => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
      }, 4000); // Reduced interval for smaller screens
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplay, carouselSlides.length]);
  
  // Handlers for navigation
  const nextSlide = () => {
    setAutoplay(false);
    setDirection(1);
    setCurrentSlide((prev) => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
  };
  
  const prevSlide = () => {
    setAutoplay(false);
    setDirection(-1);
    setCurrentSlide((prev) => (prev === 0 ? carouselSlides.length - 1 : prev - 1));
  };

  // Animation variants - Optimized for smaller screens
  const textVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: {
        duration: 0.3
      }
    })
  };

  const slideContentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.05 + 0.15,
        duration: 0.3,
        ease: "easeOut"
      }
    }),
    exit: { 
      opacity: 0, 
      y: -10,
      transition: {
        duration: 0.2
      }
    }
  };

  // Stats data - Compact for small screens
  const stats = [
    { id: 1, icon: <LocationMarkerIcon className="h-4 w-4" />, title: "+100", subtitle: "Écrans" },
    { id: 2, icon: <ChartBarIcon className="h-4 w-4" />, title: "+75%", subtitle: "Occupation" },
    { id: 3, icon: <ClockIcon className="h-4 w-4" />, title: "24h", subtitle: "Campagnes" }
  ];

  // Dynamic height for 14" screens and smaller
  const getContainerHeight = () => {
    if (windowSize.height <= 768) return 'h-[650px] max-h-[700px]'; // Very small laptops
    if (windowSize.height <= 900) return 'h-[720px] max-h-[768px]'; // 14" laptops typical
    return 'h-[800px] max-h-[900px]'; // Slightly larger 14" screens
  };

  return (
    <motion.div 
      className={`relative bg-gradient-to-br from-gray-900 to-black overflow-hidden 
                  ${getContainerHeight()} flex items-center`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Minimal background particles for performance */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {Array.from({ length: 8 }).map((_, index) => (
          <motion.div
            key={index}
            className="absolute rounded-full bg-[#E84E0E]"
            style={{
              width: Math.random() * 6 + 3 + 'px',
              height: Math.random() * 6 + 3 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              opacity: 0.08,
            }}
            animate={{
              y: [0, Math.random() * -40 - 20],
              x: [0, Math.random() * 20 - 10],
              opacity: [0.08, 0.12, 0],
              scale: [1, Math.random() * 0.2 + 1, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 8,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 4,
            }}
          />
        ))}
        
        {/* Compact gradient orbs */}
        <motion.div 
          className="absolute -top-16 right-8 w-[200px] h-[200px] rounded-full opacity-6"
          animate={{
            background: `radial-gradient(circle, ${carouselSlides[currentSlide].color}CC 0%, ${carouselSlides[currentSlide].color}00 70%)`,
            scale: [1, 1.03, 1],
            y: [0, 8, 0],
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            background: { duration: 0.5 }
          }}
        />
        
        <motion.div 
          className="absolute bottom-8 -left-16 w-[180px] h-[180px] rounded-full opacity-5"
          animate={{
            background: `radial-gradient(circle, ${carouselSlides[currentSlide].color}99 0%, ${carouselSlides[currentSlide].color}00 70%)`,
            scale: [1, 1.05, 1],
            y: [0, -8, 0],
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            background: { duration: 0.5 }
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-6 h-full flex flex-col justify-center relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 h-full">
          
          {/* Left content - Compact text */}
          <motion.div 
            className="w-full lg:w-5/12 text-white text-center lg:text-left order-2 lg:order-1"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.08
                }
              }
            }}
          >
            <motion.div 
              className="inline-flex px-3 py-1.5 rounded-full 
                         bg-gradient-to-r from-[#E84E0E]/20 to-[#E84E0E]/10 
                         backdrop-blur-sm border border-[#E84E0E]/20 mb-4"
              variants={textVariants}
              whileHover={{ scale: 1.02 }}
            >
              <motion.span
                className="text-white font-medium text-xs"
                animate={{ 
                  color: ["#ffffff", "#ffeeee", "#ffffff"],
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                Révolutionnez votre publicité extérieure
              </motion.span>
            </motion.div>
            
            <motion.h1 
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl
                         font-bold leading-tight text-white mb-4"
              variants={textVariants}
            >
              La plateforme de gestion{" "}
              <motion.span 
                className="relative inline-block"
                style={{ color: carouselSlides[currentSlide].color }}
                animate={{
                  textShadow: [
                    "0 0 2px rgba(232, 78, 14, 0)",
                    "0 0 6px rgba(232, 78, 14, 0.4)",
                    "0 0 2px rgba(232, 78, 14, 0)"
                  ],
                  color: carouselSlides[currentSlide].color
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  color: { duration: 0.5 }
                }}
              >
                digitale
                <motion.div 
                  className="absolute -bottom-1 w-full left-0 h-1"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 20" preserveAspectRatio="none">
                    <motion.path 
                      d="M0,10 Q30,18 50,10 T100,10" 
                      stroke={carouselSlides[currentSlide].color}
                      strokeOpacity={0.6}
                      strokeWidth="3" 
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ 
                        pathLength: 1,
                        stroke: carouselSlides[currentSlide].color 
                      }}
                      transition={{ 
                        delay: 0.6, 
                        duration: 0.6,
                        stroke: { duration: 0.5 }
                      }}
                    />
                  </svg>
                </motion.div>
              </motion.span>{" "}
              qui change tout
            </motion.h1>
            
            <motion.p 
              className="text-white/90 text-sm lg:text-base mb-5 max-w-lg mx-auto lg:mx-0"
              variants={textVariants}
            >
              Gérez, réservez et optimisez votre affichage publicitaire numérique en toute autonomie. 
              Notre ceinture digitale offre une couverture stratégique des zones à fort trafic.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-3 mb-6 justify-center lg:justify-start"
              variants={textVariants}
            >
              <motion.button
                className="bg-gradient-to-r text-white px-6 py-2.5 
                           rounded-full font-medium flex items-center justify-center gap-2 shadow-lg
                           text-sm"
                style={{ 
                  background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color})`,
                  boxShadow: `0 4px 8px -3px ${carouselSlides[currentSlide].color}33`
                }}
                animate={{
                  background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color})`,
                  boxShadow: `0 4px 8px -3px ${carouselSlides[currentSlide].color}33`
                }}
                transition={{ duration: 0.5 }}
                whileHover={{ 
                  scale: 1.02, 
                  boxShadow: `0 8px 12px -3px ${carouselSlides[currentSlide].color}55`
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Commencer maintenant</span>
                <ArrowRightIcon className="h-4 w-4" />
              </motion.button>
              
              <motion.button
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white 
                           border border-white/30 px-6 py-2.5 
                           rounded-full font-medium text-sm"
                whileHover={{ 
                  scale: 1.02,
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderColor: `${carouselSlides[currentSlide].color}66`
                }}
                whileTap={{ scale: 0.98 }}
              >
                Voir la démo
              </motion.button>
            </motion.div>
            
            {/* Compact stats grid */}
            <motion.div 
              className="grid grid-cols-3 gap-3"
              variants={textVariants}
            >
              {stats.map((stat, index) => (
                <motion.div 
                  key={stat.id} 
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 
                             border border-white/10 hover:border-white/30 transition-all 
                             overflow-hidden relative text-center"
                  whileHover={{ 
                    y: -2, 
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    borderColor: carouselSlides[currentSlide].color
                  }}
                  initial={{ 
                    opacity: 0, 
                    y: 15
                  }}
                  animate={{ 
                    opacity: 1, 
                    y: 0
                  }}
                  transition={{ 
                    delay: 0.15 + index * 0.08,
                    duration: 0.5,
                    ease: "easeOut"
                  }}
                >
                  <motion.div 
                    className="p-2 rounded-lg mb-2 inline-flex mx-auto"
                    style={{
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    animate={{
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {stat.icon}
                  </motion.div>
                  <div>
                    <motion.p 
                      className="font-bold text-lg text-white mb-1"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * stat.id + 0.3, duration: 0.3 }}
                    >
                      {stat.title}
                    </motion.p>
                    <motion.p 
                      className="text-white/80 text-xs"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 * stat.id + 0.5, duration: 0.3 }}
                    >
                      {stat.subtitle}
                    </motion.p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right content - Compact carousel */}
          <motion.div 
            className="w-full lg:w-7/12 order-1 lg:order-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="relative max-w-md lg:max-w-none mx-auto">
              {/* Compact screen frame */}
              <motion.div
                className="relative p-2 lg:p-3 rounded-xl overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  background: `linear-gradient(135deg, ${carouselSlides[currentSlide].color}4D 0%, rgba(255, 255, 255, 0.1) 100%)`,
                  boxShadow: `0 0 12px ${carouselSlides[currentSlide].color}4D`
                }}
                transition={{ 
                  duration: 0.5,
                  background: { duration: 0.5 },
                  boxShadow: { duration: 0.5 }
                }}
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}
                whileHover={{ 
                  y: -3,
                  boxShadow: `0 0 18px ${carouselSlides[currentSlide].color}55`
                }}
              >
                {/* Compact carousel */}
                <div className="relative w-full aspect-[16/9] overflow-hidden rounded-lg bg-black">
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={currentSlide}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="absolute inset-0 w-full h-full"
                    >
                      <motion.div 
                        className="w-full h-full bg-cover bg-center" 
                        style={{ 
                          backgroundImage: `url(${carouselSlides[currentSlide].image})`
                        }}
                        initial={{ scale: 1.03 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 2.5 }}
                      >
                        {/* Simplified gradient overlay */}
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent"
                          animate={{
                            background: `linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.6) 60%, transparent), 
                                        linear-gradient(to right, ${carouselSlides[currentSlide].color}33, transparent 60%)`
                          }}
                          transition={{ duration: 0.5 }}
                        />
                        
                        {/* Compact demo content */}
                        <motion.div 
                          className="absolute inset-0 flex flex-col justify-end p-3 lg:p-4"
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                        >
                          <AnimatePresence mode="wait">
                            <motion.div 
                              key={`slide-content-${currentSlide}`}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="text-white"
                            >
                              <motion.div 
                                className="inline-block px-2 py-1 rounded-md text-xs font-bold mb-2"
                                style={{ backgroundColor: carouselSlides[currentSlide].color }}
                                custom={1}
                                variants={slideContentVariants}
                                animate={{
                                  backgroundColor: carouselSlides[currentSlide].color,
                                }}
                                transition={{
                                  backgroundColor: { duration: 0.5 }
                                }}
                              >
                                {carouselSlides[currentSlide].badge}
                              </motion.div>
                              
                              <motion.div 
                                className="text-lg lg:text-xl font-bold mb-2 drop-shadow-lg"
                                custom={2}
                                variants={slideContentVariants}
                              >
                                {carouselSlides[currentSlide].title}
                              </motion.div>
                              
                              <motion.div 
                                className="text-xs lg:text-sm max-w-xs 
                                           bg-black/60 backdrop-blur-sm p-2 rounded-lg mb-3"
                                custom={3}
                                variants={slideContentVariants}
                              >
                                {carouselSlides[currentSlide].description}
                              </motion.div>
                              
                              {/* Compact stats */}
                              <motion.div 
                                className="flex gap-2 items-center flex-wrap"
                                custom={4}
                                variants={slideContentVariants}
                              >
                                {carouselSlides[currentSlide].stats.map((stat, idx) => (
                                  <motion.div 
                                    key={idx}
                                    className="bg-white/10 backdrop-blur-sm px-2 py-1 
                                               rounded-lg flex items-center gap-1 border border-white/10
                                               text-xs"
                                    whileHover={{
                                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                                      borderColor: carouselSlides[currentSlide].color,
                                    }}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ 
                                      opacity: 1, 
                                      y: 0,
                                      transition: {
                                        delay: 0.3 + idx * 0.05,
                                        duration: 0.3
                                      }
                                    }}
                                  >
                                    <motion.span 
                                      className="font-bold"
                                      style={{ color: carouselSlides[currentSlide].color }}
                                      animate={{ color: carouselSlides[currentSlide].color }}
                                      transition={{ duration: 0.5 }}
                                    >
                                      {stat.value}
                                    </motion.span>
                                    <span className="text-white/80">{stat.label}</span>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </motion.div>
                          </AnimatePresence>
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                  
                  {/* Compact navigation dots */}
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2 z-20">
                    {carouselSlides.map((_, index) => (
                      <motion.button
                        key={index}
                        onClick={() => {
                          setDirection(index > currentSlide ? 1 : -1);
                          setCurrentSlide(index);
                          setAutoplay(false);
                        }}
                        className="group relative"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <span 
                          className={`block w-2 h-2 rounded-full transition-all duration-300`}
                          style={{ 
                            backgroundColor: index === currentSlide 
                              ? carouselSlides[currentSlide].color
                              : 'rgba(255, 255, 255, 0.5)'
                          }}
                        />
                        {index === currentSlide && (
                          <motion.span 
                            className="absolute inset-0 w-2 h-2 rounded-full"
                            style={{ backgroundColor: carouselSlides[currentSlide].color }}
                            animate={{ 
                              scale: [1, 1.4, 1], 
                              opacity: [1, 0, 0],
                              backgroundColor: carouselSlides[currentSlide].color
                            }}
                            transition={{ 
                              duration: 1.5, 
                              repeat: Infinity,
                              backgroundColor: { duration: 0.5 }
                            }}
                          />
                        )}
                      </motion.button>
                    ))}
                  </div>
                  
                  {/* Simplified navigation arrows */}
                  <div className="absolute inset-0 items-center justify-between px-2 z-20 
                                  opacity-0 hover:opacity-100 transition-opacity duration-300 
                                  hidden lg:flex">
                    <motion.button
                      onClick={prevSlide}
                      className="w-8 h-8 flex items-center justify-center 
                                 rounded-full backdrop-blur-sm text-white"
                      animate={{ 
                        background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}CC, ${carouselSlides[currentSlide].color}99)`
                      }}
                      transition={{ duration: 0.5 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </motion.button>
                    
                    <motion.button
                      onClick={nextSlide}
                      className="w-8 h-8 flex items-center justify-center 
                                 rounded-full backdrop-blur-sm text-white"
                      animate={{ 
                        background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}CC, ${carouselSlides[currentSlide].color}99)`
                      }}
                      transition={{ duration: 0.5 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </motion.button>
                  </div>
                </div>
                
                {/* Simplified control bar */}
                <div className="flex justify-between items-center mt-2 px-1 z-10 relative">
                  <div className="flex items-center space-x-2">
                    <motion.div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: carouselSlides[currentSlide].color }}
                      animate={{ 
                        opacity: [1, 0.4, 1],
                        backgroundColor: carouselSlides[currentSlide].color,
                      }}
                      transition={{ 
                        duration: 1.5,
                        repeat: Infinity,
                        backgroundColor: { duration: 0.5 }
                      }}
                    />
                    <div className="text-xs text-white">LIVE</div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="h-1 w-16 bg-white/20 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full"
                        style={{ 
                          background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color})`
                        }}
                        animate={{
                          width: ["0%", "100%"],
                          background: `linear-gradient(to right, ${carouselSlides[currentSlide].color}, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color})`
                        }}
                        transition={{
                          width: {
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity
                          },
                          background: { duration: 0.5 }
                        }}
                      />
                    </div>
                    <div className="text-xs text-white">En diffusion</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Compact floating elements - Only on larger screens within 14" */}
              <motion.div
                className="absolute -right-2 top-1/4 
                           backdrop-blur-md rounded-lg p-2 shadow-lg z-20
                           hidden lg:block"
                style={{ 
                  backgroundColor: `${carouselSlides[currentSlide].color}1A`,
                  borderColor: `${carouselSlides[currentSlide].color}33`,
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
                initial={{ opacity: 0, scale: 0.8, x: 15 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  x: 0,
                  backgroundColor: `${carouselSlides[currentSlide].color}1A`,
                  borderColor: `${carouselSlides[currentSlide].color}33`
                }}
                transition={{ 
                  delay: 0.6, 
                  duration: 0.4,
                  backgroundColor: { duration: 0.5 },
                  borderColor: { duration: 0.5 }
                }}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: `0 6px 15px -5px ${carouselSlides[currentSlide].color}4D`
                }}
              >
                <div className="flex items-center space-x-2">
                  <motion.div 
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    animate={{ 
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <LocationMarkerIcon className="h-3 w-3" />
                  </motion.div>
                  <div>
                    <div className="text-white font-medium text-xs">Centre Commercial</div>
                    <div className="text-white/80 text-xs">15 écrans</div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                className="absolute -left-2 top-1/4 
                           backdrop-blur-md rounded-lg p-2 shadow-lg z-20
                           hidden lg:block"
                style={{ 
                  backgroundColor: `${carouselSlides[currentSlide].color}1A`,
                  borderColor: `${carouselSlides[currentSlide].color}33`,
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
                initial={{ opacity: 0, scale: 0.8, x: -15 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  x: 0,
                  backgroundColor: `${carouselSlides[currentSlide].color}1A`,
                  borderColor: `${carouselSlides[currentSlide].color}33`
                }}
                transition={{ 
                  delay: 0.8, 
                  duration: 0.4,
                  backgroundColor: { duration: 0.5 },
                  borderColor: { duration: 0.5 }
                }}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: `0 6px 15px -5px ${carouselSlides[currentSlide].color}4D`
                }}
              >
                <div className="flex items-center space-x-2">
                  <motion.div 
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    animate={{ 
                      backgroundColor: `${carouselSlides[currentSlide].color}33`,
                      color: carouselSlides[currentSlide].color
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {carouselSlides[currentSlide].icon}
                  </motion.div>
                  <div>
                    <div className="text-white font-medium text-xs">+25K</div>
                    <div className="text-white/80 text-xs">Impressions/jour</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Compact floating badge */}
              <motion.div
                className="absolute -right-1 -top-1 flex items-center justify-center"
                initial={{ opacity: 0, scale: 0, rotate: -8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 1, duration: 0.4 }}
              >
                <motion.div
                  className="w-10 h-10 rounded-full flex flex-col items-center justify-center text-white relative"
                  style={{ 
                    background: `linear-gradient(45deg, ${carouselSlides[currentSlide].color}E6, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color}E6)`,
                    boxShadow: `0 4px 10px -5px ${carouselSlides[currentSlide].color}66`
                  }}
                  animate={{ 
                    background: `linear-gradient(45deg, ${carouselSlides[currentSlide].color}E6, ${carouselSlides[currentSlide === carouselSlides.length - 1 ? 0 : currentSlide + 1].color}E6)`,
                    boxShadow: `0 4px 10px -5px ${carouselSlides[currentSlide].color}66`
                  }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 5
                  }}
                >
                  <motion.span className="text-sm font-bold">{currentSlide + 1}</motion.span>
                  <motion.span className="text-xs">/ {carouselSlides.length}</motion.span>
                  
                  {/* Simplified decorative ring */}
                  <motion.div 
                    className="absolute inset-0 rounded-full border border-white/30"
                    animate={{ 
                      rotate: [0, 360],
                      borderColor: [`${carouselSlides[currentSlide].color}4D`, "rgba(255,255,255,0.3)", `${carouselSlides[currentSlide].color}4D`],
                    }}
                    transition={{ 
                      rotate: { duration: 12, repeat: Infinity, ease: "linear" },
                      borderColor: { duration: 2, repeat: Infinity }
                    }}
                  />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Compact scroll indicator */}
      <motion.div 
        className="absolute bottom-3 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.5 }}
      >
        <motion.div 
          className="flex flex-col items-center cursor-pointer"
          whileHover={{ scale: 1.03 }}
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }}
        >
          <motion.span 
            className="text-white/70 text-xs mb-1"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Découvrir plus
          </motion.span>
          <motion.div
            className="w-5 h-8 border-2 rounded-full flex justify-center p-1"
            style={{ borderColor: carouselSlides[currentSlide].color }}
            animate={{ borderColor: carouselSlides[currentSlide].color }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="w-1 h-1 rounded-full"
              style={{ backgroundColor: carouselSlides[currentSlide].color }}
              animate={{ 
                backgroundColor: carouselSlides[currentSlide].color,
                y: [0, 8, 0]
              }}
              transition={{ 
                y: { duration: 1, repeat: Infinity },
                backgroundColor: { duration: 0.5 }
              }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default HeroSection;